import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ImageSelectDialog from '../Components/ImageSelectDialog';

function SubCategoryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    subtitle: '',
    description: '',
    label: '',
    image: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        const [subCatRes, catRes] = await Promise.all([
          fetch(`${config.apiUrl}/api/v1/admin/subcategories?disable_pagination=true`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!subCatRes.ok) throw new Error('Failed to fetch subcategories');
        if (!catRes.ok) throw new Error('Failed to fetch categories');

        const subCats = await subCatRes.json();
        const cats = await catRes.json();

        setCategories(cats);

        const subCat = subCats.find(s => String(s.sub_category_id) === String(id));
        if (!subCat) throw new Error('Subcategory not found');

        setFormData({
          category_id: subCat.category_id || '',
          title: subCat.title || '',
          subtitle: subCat.subtitle || '',
          description: subCat.description || '',
          label: subCat.label || '',
          image: subCat.image || '',
        });
        setImagePreview(subCat.image || '');
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const uploadResponse = await fetch(
          'https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload',
          {
            method: 'POST',
            headers: { 'X-Client': 'ADMIN', Authorization: `Bearer ${token}` },
            body: fd
          }
        );
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success('Image uploaded successfully!');
        } else {
          throw new Error('Failed to upload image');
        }
      }

      const payload = { ...formData, image: imageUrl };
      Object.keys(payload).forEach(key => {
        if (typeof payload[key] === 'string' && payload[key].trim() === '') {
          payload[key] = null;
        }
      });

      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/subcategories/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) throw new Error('Failed to update subcategory');
      toast.success('Subcategory updated successfully!');
      setTimeout(() => navigate('/subcategories'), 1000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/subcategories')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-md transition-all duration-200 hover:opacity-90 hover:shadow-lg active:scale-95"
            style={{ background: 'linear-gradient(135deg, #002F53 0%, #004a82 100%)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Subcategories
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Subcategory</h1>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Refrigerator Repair"
                  required
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Fast, reliable repairs for refrigerator"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. NEW, QUICK etc."
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
                {imagePreview && (
                  <div className="mb-3">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-md border border-gray-200"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowImageSelectDialog(true)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </button>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => navigate('/subcategories')}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 text-white rounded-md font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: '#002F53' }}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>

      <ImageSelectDialog
        open={showImageSelectDialog}
        onClose={() => setShowImageSelectDialog(false)}
        onFileProcessed={(file) => {
          handleImageSelect(file);
          setShowImageSelectDialog(false);
        }}
      />
    </div>
  );
}

export default SubCategoryEdit;
